(() => {
  const SELECTOR = '.ns-home[data-section-id]';

  function initCarousel(root, name) {
    const viewport = root.querySelector(`[data-carousel="${name}"]`);
    if (!viewport || viewport.dataset.ready === 'true') return;

    const prev = root.querySelector(`[data-carousel-prev="${name}"]`);
    const next = root.querySelector(`[data-carousel-next="${name}"]`);
    const status = root.querySelector(`[data-carousel-status="${name}"]`);
    const chrome = status?.closest('.ns-carousel-chrome');
    const cards = Array.from(viewport.children);

    if (cards.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
      return;
    }

    const cardStep = () => {
      const first = cards[0];
      const styles = window.getComputedStyle(viewport);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      return first.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      // A row that already fits is not a four-page carousel.
      if (chrome) chrome.hidden = max <= 2;
      if (prev) prev.disabled = viewport.scrollLeft <= 2;
      if (next) next.disabled = viewport.scrollLeft >= max - 2;
      if (status) {
        const bounds = viewport.getBoundingClientRect();
        const visible = cards.map((card, index) => ({ index, rect: card.getBoundingClientRect() }))
          .filter(({ rect }) => rect.right > bounds.left + 8 && rect.left < bounds.right - 8);
        const first = (visible[0]?.index ?? 0) + 1;
        const last = (visible[visible.length - 1]?.index ?? 0) + 1;
        const pad = (number) => String(number).padStart(2, '0');
        status.textContent = `${pad(first)}${last > first ? '–' + pad(last) : ''} / ${pad(cards.length)}`;
      }
    };

    const scroll = (direction) => {
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      viewport.scrollTo({
        left: Math.max(0, Math.min(max, viewport.scrollLeft + direction * cardStep())),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    };

    prev?.addEventListener('click', () => {
      scroll(-1);
    });
    next?.addEventListener('click', () => {
      scroll(1);
    });
    viewport.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(viewport);
    viewport.dataset.ready = 'true';
    update();
  }

  function escapeHtml(value) {
    const node = document.createElement('div');
    node.textContent = value || '';
    return node.innerHTML;
  }

  function stripHtml(value) {
    const node = document.createElement('div');
    node.innerHTML = value || '';
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  const journalLinkOverrides = {
    'hygge-bosai-goods-award-2026-triple-award':
      '/blogs/%E3%81%8A%E7%9F%A5%E3%82%89%E3%81%9B/hygge-bosai-goods-award-2026-triple-award'
  };

  function resolveJournalLink(post) {
    const override = journalLinkOverrides[post?.slug];
    return override
      ? { href: override, attributes: '' }
      : { href: post?.link || '#', attributes: ' target="_blank" rel="noopener"' };
  }

  // Article-specific media coverage thumbnails; other posts retain their featured images.
  const journalThumbnailOverrides = {
  "nihon-setsubi-waterbag-2026-09-25": "data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22720%22%20height%3D%22720%22%20viewBox%3D%220%200%20720%20720%22%3E%3Crect%20width%3D%22720%22%20height%3D%22720%22%20fill%3D%22%23F1F2EB%22%2F%3E%3Crect%20width%3D%22720%22%20height%3D%2210%22%20fill%3D%22%2329473C%22%2F%3E%3Cg%20fill%3D%22%2329473C%22%20font-family%3D%22Arial%2C%20'Noto%20Sans%20JP'%2C%20'Yu%20Gothic'%2C%20Meiryo%2C%20sans-serif%22%3E%3Ctext%20x%3D%2256%22%20y%3D%2280%22%20font-size%3D%2220%22%20letter-spacing%3D%223%22%20font-weight%3D%22700%22%3EMEDIA%20COVERAGE%3C%2Ftext%3E%3Cg%20fill%3D%22none%22%20stroke%3D%22%2329473C%22%20stroke-width%3D%223.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Crect%20x%3D%22568%22%20y%3D%2272%22%20width%3D%2278%22%20height%3D%2294%22%20rx%3D%224%22%2F%3E%3Cpath%20d%3D%22M582%2093h50M582%20107h50M582%20124h18v26h-18zM612%20124h20M612%20137h20M612%20150h20%22%2F%3E%3C%2Fg%3E%3Ctext%20x%3D%2256%22%20y%3D%22192%22%20font-size%3D%2226%22%20font-weight%3D%22700%22%3E%E6%96%B0%E8%81%9E%E6%8E%B2%E8%BC%89%3C%2Ftext%3E%3Ctext%20x%3D%2252%22%20y%3D%22304%22%20font-size%3D%2278%22%20font-weight%3D%22700%22%3E%E6%97%A5%E6%9C%AC%E8%A8%AD%E5%82%99%3C%2Ftext%3E%3Ctext%20x%3D%2252%22%20y%3D%22398%22%20font-size%3D%2278%22%20font-weight%3D%22700%22%3E%E5%B7%A5%E6%A5%AD%E6%96%B0%E8%81%9E%3C%2Ftext%3E%3Cpath%20d%3D%22M56%20454H664%22%20stroke%3D%22%2329473C%22%20stroke-opacity%3D%22.28%22%2F%3E%3Ctext%20x%3D%2256%22%20y%3D%22510%22%20font-size%3D%2227%22%20font-weight%3D%22700%22%3E%E3%83%95%E3%83%AC%E3%83%83%E3%82%AF%E3%82%B9%E3%82%A6%E3%82%A9%E3%83%BC%E3%82%BF%E3%83%BC%E3%83%90%E3%83%83%E3%82%B0%3C%2Ftext%3E%3Ctext%20x%3D%2256%22%20y%3D%22557%22%20font-size%3D%2225%22%3E%E6%8E%B2%E8%BC%89%E3%81%95%E3%82%8C%E3%81%BE%E3%81%97%E3%81%9F%3C%2Ftext%3E%3Ctext%20x%3D%2256%22%20y%3D%22665%22%20font-size%3D%2223%22%20letter-spacing%3D%222%22%3E2026.09.25%3C%2Ftext%3E%3Ctext%20x%3D%22664%22%20y%3D%22665%22%20text-anchor%3D%22end%22%20font-size%3D%2223%22%20font-weight%3D%22700%22%20letter-spacing%3D%223%22%3ENESEKT%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fsvg%3E",
  "goodmorning-waterbag-2026-09-17": "data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22720%22%20height%3D%22720%22%20viewBox%3D%220%200%20720%20720%22%3E%3Crect%20width%3D%22720%22%20height%3D%22720%22%20fill%3D%22%23FAF0E3%22%2F%3E%3Crect%20width%3D%22720%22%20height%3D%2210%22%20fill%3D%22%23955129%22%2F%3E%3Cg%20fill%3D%22%23955129%22%20font-family%3D%22Arial%2C%20'Noto%20Sans%20JP'%2C%20'Yu%20Gothic'%2C%20Meiryo%2C%20sans-serif%22%3E%3Ctext%20x%3D%2256%22%20y%3D%2280%22%20font-size%3D%2220%22%20letter-spacing%3D%223%22%20font-weight%3D%22700%22%3EMEDIA%20COVERAGE%3C%2Ftext%3E%3Cg%20fill%3D%22none%22%20stroke%3D%22%23955129%22%20stroke-width%3D%223.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Ccircle%20cx%3D%22608%22%20cy%3D%22120%22%20r%3D%2226%22%2F%3E%3Cpath%20d%3D%22M608%2073v-10M608%20167v10M561%20120h-10M655%20120h10M575%2087l-8-8M641%20153l8%208M575%20153l-8%208M641%2087l8-8%22%2F%3E%3C%2Fg%3E%3Ctext%20x%3D%2256%22%20y%3D%22192%22%20font-size%3D%2226%22%20font-weight%3D%22700%22%3E%E3%83%86%E3%83%AC%E3%83%93%E6%9C%9D%E6%97%A5%E7%B3%BB%E5%88%97%3C%2Ftext%3E%3Ctext%20x%3D%2252%22%20y%3D%22304%22%20font-size%3D%2272%22%20font-weight%3D%22700%22%3E%E3%82%B0%E3%83%83%E3%83%89%EF%BC%81%3C%2Ftext%3E%3Ctext%20x%3D%2252%22%20y%3D%22398%22%20font-size%3D%2272%22%20font-weight%3D%22700%22%3E%E3%83%A2%E3%83%BC%E3%83%8B%E3%83%B3%E3%82%B0%3C%2Ftext%3E%3Cpath%20d%3D%22M56%20454H664%22%20stroke%3D%22%23955129%22%20stroke-opacity%3D%22.28%22%2F%3E%3Ctext%20x%3D%2256%22%20y%3D%22510%22%20font-size%3D%2227%22%20font-weight%3D%22700%22%3E%E3%83%95%E3%83%AC%E3%83%83%E3%82%AF%E3%82%B9%E3%82%A6%E3%82%A9%E3%83%BC%E3%82%BF%E3%83%BC%E3%83%90%E3%83%83%E3%82%B0%3C%2Ftext%3E%3Ctext%20x%3D%2256%22%20y%3D%22557%22%20font-size%3D%2225%22%3E%E7%95%AA%E7%B5%84%E3%81%A7%E7%B4%B9%E4%BB%8B%E3%81%95%E3%82%8C%E3%81%BE%E3%81%97%E3%81%9F%3C%2Ftext%3E%3Ctext%20x%3D%2256%22%20y%3D%22665%22%20font-size%3D%2223%22%20letter-spacing%3D%222%22%3E2026.09.17%3C%2Ftext%3E%3Ctext%20x%3D%22664%22%20y%3D%22665%22%20text-anchor%3D%22end%22%20font-size%3D%2223%22%20font-weight%3D%22700%22%20letter-spacing%3D%223%22%3ENESEKT%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fsvg%3E",
  "nhk-ohayou-waterbag-2026-09-03": "data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22720%22%20height%3D%22720%22%20viewBox%3D%220%200%20720%20720%22%3E%3Crect%20width%3D%22720%22%20height%3D%22720%22%20fill%3D%22%23EAF0F6%22%2F%3E%3Crect%20width%3D%22720%22%20height%3D%2210%22%20fill%3D%22%232C4D70%22%2F%3E%3Cg%20fill%3D%22%232C4D70%22%20font-family%3D%22Arial%2C%20'Noto%20Sans%20JP'%2C%20'Yu%20Gothic'%2C%20Meiryo%2C%20sans-serif%22%3E%3Ctext%20x%3D%2256%22%20y%3D%2280%22%20font-size%3D%2220%22%20letter-spacing%3D%223%22%20font-weight%3D%22700%22%3EMEDIA%20COVERAGE%3C%2Ftext%3E%3Cg%20fill%3D%22none%22%20stroke%3D%22%232C4D70%22%20stroke-width%3D%223.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Crect%20x%3D%22562%22%20y%3D%2286%22%20width%3D%2294%22%20height%3D%2264%22%20rx%3D%227%22%2F%3E%3Cpath%20d%3D%22M585%20168h48M609%20150v18%22%2F%3E%3Cpath%20d%3D%22M581%20122v10M595%20111v21M609%20101v31M623%20115v17M637%20106v26%22%2F%3E%3C%2Fg%3E%3Ctext%20x%3D%2256%22%20y%3D%22192%22%20font-size%3D%2226%22%20font-weight%3D%22700%22%3ENHK%E7%B7%8F%E5%90%88%3C%2Ftext%3E%3Ctext%20x%3D%2252%22%20y%3D%22304%22%20font-size%3D%2264%22%20font-weight%3D%22700%22%3ENHK%E3%83%8B%E3%83%A5%E3%83%BC%E3%82%B9%3C%2Ftext%3E%3Ctext%20x%3D%2252%22%20y%3D%22398%22%20font-size%3D%2264%22%20font-weight%3D%22700%22%3E%E3%81%8A%E3%81%AF%E3%82%88%E3%81%86%E6%97%A5%E6%9C%AC%3C%2Ftext%3E%3Cpath%20d%3D%22M56%20454H664%22%20stroke%3D%22%232C4D70%22%20stroke-opacity%3D%22.28%22%2F%3E%3Ctext%20x%3D%2256%22%20y%3D%22510%22%20font-size%3D%2227%22%20font-weight%3D%22700%22%3E%E3%83%95%E3%83%AC%E3%83%83%E3%82%AF%E3%82%B9%E3%82%A6%E3%82%A9%E3%83%BC%E3%82%BF%E3%83%BC%E3%83%90%E3%83%83%E3%82%B0%3C%2Ftext%3E%3Ctext%20x%3D%2256%22%20y%3D%22557%22%20font-size%3D%2225%22%3E%E7%95%AA%E7%B5%84%E3%81%A7%E7%B4%B9%E4%BB%8B%E3%81%95%E3%82%8C%E3%81%BE%E3%81%97%E3%81%9F%3C%2Ftext%3E%3Ctext%20x%3D%2256%22%20y%3D%22665%22%20font-size%3D%2223%22%20letter-spacing%3D%222%22%3E2026.09.03%3C%2Ftext%3E%3Ctext%20x%3D%22664%22%20y%3D%22665%22%20text-anchor%3D%22end%22%20font-size%3D%2223%22%20font-weight%3D%22700%22%20letter-spacing%3D%223%22%3ENESEKT%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fsvg%3E"
};

  // Store policy: recruitment must never be imported or published here.
  // Only reviewed product/media articles may be added to this explicit allowlist.
  // New company posts are excluded until reviewed, even if their titles look harmless.
  const approvedJournalPosts = new Map([
    [905, 'nihon-setsubi-waterbag-2026-09-25'],
    [894, 'goodmorning-waterbag-2026-09-17'],
    [887, 'nhk-ohayou-waterbag-2026-09-03'],
    [880, 'nonstop-waterbag-2026-08-24'],
    [864, 'hygge-bosai-goods-award-2026-triple-award']
  ]);
  const journalRecruitmentTitlePattern = /求人|募集|採用|アルバイト|パート|正社員|契約社員|派遣社員/;
  const journalRecruitmentDetailsPattern = /求人|採用情報|採用案内|採用選考|募集要項|応募資格|雇用形態|正社員|契約社員|派遣社員|時給|月給|履歴書|職務経歴書|中途採用|新卒採用/;

  function isJournalPostAllowed(post) {
    const approvedSlug = approvedJournalPosts.get(post?.id);
    if (!approvedSlug || post.slug !== approvedSlug || post.status !== 'publish') return false;
    try {
      const link = new URL(post.link);
      if (link.protocol !== 'https:' || link.hostname !== 'www.nesekt.co.jp'
        || link.pathname !== '/' + approvedSlug + '/') return false;
    } catch (_) {
      return false;
    }
    const title = stripHtml(post.title?.rendered);
    const details = stripHtml((post.excerpt?.rendered || '') + ' ' + (post.content?.rendered || ''));
    return Boolean(title) && !journalRecruitmentTitlePattern.test(title)
      && !journalRecruitmentDetailsPattern.test(details);
  }

  async function loadJournal(root) {
    const grid = root.querySelector('[data-journal-grid]');
    const snap = root.querySelector('[data-snap-journal]');
    if ((!grid && !snap) || root.dataset.journalReady === 'true') return;
    root.dataset.journalReady = 'true';

    try {
      const response = await fetch(
        'https://www.nesekt.co.jp/wp-json/wp/v2/posts?include=' + Array.from(approvedJournalPosts.keys()).join(',') + '&per_page=5&orderby=date&order=desc&_embed=wp:featuredmedia',
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const posts = await response.json();
      if (!Array.isArray(posts) || !posts.length) throw new Error('No posts');

      const selectedPosts = posts.filter(isJournalPostAllowed).slice(0, 3);

      if (snap) {
        snap.innerHTML = selectedPosts.map((post, index) => {
          const media = post?._embedded?.['wp:featuredmedia']?.[0];
          const image = journalThumbnailOverrides[post?.slug] || media?.media_details?.sizes?.large?.source_url
            || media?.media_details?.sizes?.medium_large?.source_url
            || media?.source_url
            || '';
          const title = stripHtml(post?.title?.rendered);
          const link = resolveJournalLink(post);

          return `
            <a class="ns-snap-card ns-snap-card--journal" href="${escapeHtml(link.href)}"${link.attributes}>
              <div class="ns-snap-card__media">
                ${image
                  ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">`
                  : '<span class="ns-snap-card__fallback">JOURNAL</span>'}
              </div>
              <span>${escapeHtml(title)}</span>
              <small>${String(index + 1).padStart(2, '0')}</small>
            </a>`;
        }).join('');
        snap.dataset.ready = 'true';
      }

      if (grid) {
        grid.innerHTML = selectedPosts.map((post) => {
          const media = post?._embedded?.['wp:featuredmedia']?.[0];
          const image = journalThumbnailOverrides[post?.slug] || media?.media_details?.sizes?.medium_large?.source_url || media?.source_url || '';
          const title = stripHtml(post?.title?.rendered);
          const excerpt = stripHtml(post?.excerpt?.rendered).slice(0, 90);
          const link = resolveJournalLink(post);
          const date = new Intl.DateTimeFormat('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).format(new Date(post.date));

          return `
            <article class="ns-journal-card">
              <a class="ns-journal-card__image" href="${escapeHtml(link.href)}"${link.attributes}>
                ${image
                  ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">`
                  : '<span class="ns-journal-card__fallback">JOURNAL</span>'}
              </a>
              <div class="ns-journal-card__body">
                <time datetime="${escapeHtml(post.date)}">${escapeHtml(date)}</time>
                <h3><a href="${escapeHtml(link.href)}"${link.attributes}>${escapeHtml(title)}</a></h3>
                <p>${escapeHtml(excerpt)}${excerpt.length >= 90 ? '…' : ''}</p>
                <a class="ns-text-link" href="${escapeHtml(link.href)}"${link.attributes}>記事を読む <span aria-hidden="true">→</span></a>
              </div>
            </article>`;
        }).join('');
        grid.dataset.ready = 'true';
      }
    } catch (error) {
      grid?.classList.add('ns-journal-grid--fallback');
      snap?.classList.add('ns-snap__track--fallback');
    }
  }

  function init(root) {
    ['hero', 'ranking', 'new'].forEach((name) => initCarousel(root, name));
    loadJournal(root);
  }

  document.querySelectorAll(SELECTOR).forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    if (root) init(root);
  });
})();
